import { useQuery } from '@tanstack/react-query';
import type { NotificationListDto } from '@cpm/shared';
import { Bell } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { api } from '../api/client.js';
import { cx } from './ui.js';

/** Poll the unread count cheaply (one item page carries the counter). */
export function useUnreadNotifications() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<NotificationListDto>('/api/notifications/?perPage=1'),
    refetchInterval: 60_000,
    select: (data) => data.unreadCount,
  });
}

/** Sidebar entry for the staff notification feed, with an unread badge. */
export function NotificationsNavItem({ onNavigate }: { onNavigate?: () => void }) {
  const unread = useUnreadNotifications();
  const count = unread.data ?? 0;

  return (
    <NavLink
      to="/notifications"
      onClick={onNavigate}
      className={({ isActive }) =>
        cx(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white',
        )
      }
    >
      <Bell className="size-4.5 shrink-0" aria-hidden />
      <span className="flex-1">Notifications</span>
      {count > 0 && (
        <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-brand-600 px-1.5 py-0.5 text-xs font-semibold text-white">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </NavLink>
  );
}
